import React, { useState } from 'react';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-premium';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import { RtkQueryTable, RtkQueryTableModels } from '../../../../components/Table/RtkQueryTable';
import { useFetchUserDividendsQuery } from '../../../../redux/features/alpacaFee/alpacaFee.apiSlice';
import { Dividend, DividendStatus } from '../../../../types/alpacaFee';
import { round2Format } from '../../../../utils/number';

const usd = (value: number | null | undefined) => {
  if (value === null || value === undefined) {
    return '';
  }

  return `$${round2Format(Number(value))}`;
};

/** The buy a row went into — or, for a leftover row, the buy it was left over from. */
const buyOf = (row: Dividend) => row.dividendOrder || row.leftoverOfDividendOrder;

const COLUMNS: GridColDef<Dividend>[] = [
  { field: 'id', headerName: 'Id', width: 70, sortable: false },
  {
    field: 'dividendDate',
    headerName: 'Date',
    type: 'date',
    width: 110,
    sortable: false,
    valueGetter: ({ value }) => (value ? new Date(value) : null),
  },
  { field: 'symbol', headerName: 'Fund', width: 90, sortable: false },
  {
    field: 'kind',
    headerName: 'Type',
    width: 110,
    sortable: false,
    valueGetter: ({ row }) => (row.leftoverOfDividendOrderId ? 'Leftover' : 'Dividend'),
  },
  {
    field: 'amount',
    headerName: 'Amount',
    type: 'number',
    width: 100,
    sortable: false,
    renderCell: ({ value }) => usd(value),
  },
  {
    field: 'status',
    headerName: 'Status',
    width: 120,
    sortable: false,
    renderCell: ({ value }: GridRenderCellParams<Dividend>) => (
      <Chip
        size="small"
        label={value}
        color={value === DividendStatus.REINVESTED ? 'success' : 'warning'}
        variant="outlined"
      />
    ),
  },
  {
    field: 'buyId',
    headerName: 'Buy',
    width: 80,
    sortable: false,
    valueGetter: ({ row }) => buyOf(row)?.id ?? '',
  },
  {
    field: 'buyStatus',
    headerName: 'Buy status',
    width: 120,
    sortable: false,
    valueGetter: ({ row }) => buyOf(row)?.status ?? '',
  },
  {
    field: 'buyAmount',
    headerName: 'Buy total',
    type: 'number',
    width: 100,
    sortable: false,
    renderCell: ({ row }: GridRenderCellParams<Dividend>) => usd(buyOf(row)?.amount),
  },
  {
    field: 'buyCat',
    headerName: 'CAT',
    type: 'number',
    width: 80,
    sortable: false,
    renderCell: ({ row }: GridRenderCellParams<Dividend>) => usd(buyOf(row)?.catFee),
  },
  {
    field: 'buyOrdered',
    headerName: 'Ordered',
    width: 120,
    sortable: false,
    valueGetter: ({ row }) => {
      const buy = buyOf(row);

      if (!buy) return '';

      return buy.qty ? `${buy.qty} shares` : usd(buy.notional);
    },
  },
  {
    field: 'buyFilled',
    headerName: 'Filled',
    width: 150,
    sortable: false,
    valueGetter: ({ row }) => {
      const buy = buyOf(row);

      if (!buy?.filledQty) return '';

      return `${buy.filledQty} @ ${usd(buy.filledAvgPrice)}`;
    },
  },
  {
    field: 'buyRetries',
    headerName: 'Retries',
    type: 'number',
    width: 80,
    sortable: false,
    valueGetter: ({ row }) => buyOf(row)?.retryCount ?? '',
  },
];

interface AlpacaDividendsTableProps {
  userId: number;
}

/**
 * The user's dividends: each one received (or the leftover cash of a buy), whether it's still waiting (`pending`) or
 * was bought (`reinvested`), and the buy it went into. Newest first.
 */
export function AlpacaDividendsTable({ userId }: AlpacaDividendsTableProps) {
  const [tableModels, setTableModels] = useState<RtkQueryTableModels>({
    paginationModel: { page: 0, pageSize: 20 },
  });
  const { pageSize, page } = tableModels.paginationModel;
  const { data, isFetching, isError } = useFetchUserDividendsQuery({
    userId,
    limit: pageSize,
    offset: page * pageSize,
  });

  if (isError) {
    return <Alert severity="error">Could not load the dividends.</Alert>;
  }

  return (
    <RtkQueryTable
      models={tableModels}
      onModelsChange={setTableModels}
      rows={data?.items || []}
      columns={COLUMNS}
      loading={isFetching}
      rowCount={data?.totalItems || 0}
      pageSizeOptions={[20, 50, 100]}
      disableColumnFilter
      disableColumnMenu
      wrapperHeight={400}
    />
  );
}
